import { useEffect, useRef } from "react";
import ImageUpload from "../../../components/cms/ImageUpload";
import { TRANSLATIONS } from "../../../contexts/LanguageContext";
import useSettings from "./useSettings";
import { SettingsHeader, Section, Field, Loading, MultiLangField } from "./_shared";

// Home settings that get prefilled from the built-in i18n dictionary the first
// time the CMS is opened. Each entry: [ settings prefix, i18n path ].
// The prefill covers all 4 languages (es · fr · en · ar).
const HOME_PREFILL = [
  ["home_welcome_kicker",   "home.welcomeKicker"],
  ["home_welcome_title",    "home.welcomeTitle"],
  ["home_welcome_body",     "home.welcomeBody"],
  ["home_manifesto_kicker", "home.manifestoKicker"],
  ["home_manifesto_quote",  "home.manifestoTitle"],
  ["home_manifesto_body",   "home.manifestoBody"],
  ["home_stats_title",      "home.statsTitle"],
  ["home_benefits_kicker",  "home.benefitsKicker"],
  ["home_benefits_title",   "home.benefitsTitle"],
  ["home_welcome_cta_label","nav.camaraOverview"],
  ["home_benefits_cta_label","nav.becomeMember"],
];

const YEAR_LABEL_DEFAULTS = {
  home_welcome_year_label_es: "Año de fundación",
  home_welcome_year_label_fr: "Année de fondation",
  home_welcome_year_label_en: "Founding year",
  home_welcome_year_label_ar: "سنة التأسيس",
};

const LANGS = ["es", "fr", "en", "ar"];

function resolve(path, lang) {
  const segs = path.split(".");
  let cur = TRANSLATIONS[lang];
  for (const s of segs) {
    if (cur == null) return "";
    cur = cur[s];
  }
  return cur ?? "";
}

export default function HomeSettingsAdmin() {
  const { settings, setSettings, set, save, saving, saved } = useSettings();
  const prefilledRef = useRef(false);

  // Once settings load, seed empty fields with the strings currently rendered
  // on the public site. Runs once per session so the editor never overwrites
  // saved DB values.
  useEffect(() => {
    if (!settings || prefilledRef.current) return;
    prefilledRef.current = true;
    const patch = {};
    for (const [prefix, tPath] of HOME_PREFILL) {
      for (const L of LANGS) {
        const key = `${prefix}_${L}`;
        if (!settings[key] || !String(settings[key]).trim()) {
          patch[key] = resolve(tPath, L);
        }
      }
    }
    for (const [k, v] of Object.entries(YEAR_LABEL_DEFAULTS)) {
      if (!settings[k] || !String(settings[k]).trim()) patch[k] = v;
    }
    if (!settings.home_welcome_year) patch.home_welcome_year = "1886";
    if (!settings.home_welcome_cta_url) patch.home_welcome_cta_url = "/la-camara";
    if (Object.keys(patch).length) setSettings((prev) => ({ ...prev, ...patch }));
  }, [settings, setSettings]);

  if (!settings) return <Loading />;

  return (
    <div data-testid="cms-settings-home">
      <SettingsHeader
        title="Textos e imágenes de la portada"
        description="Todos los bloques editables de la home. Cada campo ofrece 4 idiomas — pulse una pestaña para editar ES · FR · EN · AR."
        onSave={save}
        saving={saving}
        saved={saved}
      />

      <div className="space-y-6">
        <Section title="Estadísticas (portada)">
          <div className="grid md:grid-cols-4 gap-4">
            <Field label="Años" type="number" value={settings.stat_years} onChange={(v) => set("stat_years", parseInt(v || 0))} />
            <Field label="Socios" type="number" value={settings.stat_members} onChange={(v) => set("stat_members", parseInt(v || 0))} />
            <Field label="Actividades" type="number" value={settings.stat_activities} onChange={(v) => set("stat_activities", parseInt(v || 0))} />
            <Field label="Empresas" type="number" value={settings.stat_companies} onChange={(v) => set("stat_companies", parseInt(v || 0))} />
          </div>
        </Section>

        <Section title="Bloque de bienvenida">
          <p className="text-xs text-gray-500 mb-3">Pulse cada pestaña de idioma para editar la traducción correspondiente.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <MultiLangField label="Kicker" prefix="home_welcome_kicker" settings={settings} set={set} />
            <MultiLangField label="Título" prefix="home_welcome_title" settings={settings} set={set} />
            <div className="md:col-span-2">
              <MultiLangField label="Texto" prefix="home_welcome_body" settings={settings} set={set} textarea />
            </div>
            <div className="md:col-span-2">
              <ImageUpload
                label="Imagen (aspecto 4:5)"
                aspect="square"
                value={settings.home_welcome_image ?? ""}
                onChange={(v) => set("home_welcome_image", v)}
                focalX={settings.home_welcome_image_focal_x ?? 50}
                focalY={settings.home_welcome_image_focal_y ?? 50}
                onFocalChange={({ focal_x, focal_y }) => setSettings((prev) => ({
                  ...prev,
                  home_welcome_image_focal_x: focal_x,
                  home_welcome_image_focal_y: focal_y,
                }))}
                help="Suba la foto y haga clic sobre el punto que debe quedar centrado en la portada."
              />
            </div>
            <Field label="Año destacado" value={settings.home_welcome_year} onChange={(v) => set("home_welcome_year", v)} />
            <div />
            <MultiLangField label="Etiqueta año" prefix="home_welcome_year_label" settings={settings} set={set} />
            <MultiLangField label="Texto botón" prefix="home_welcome_cta_label" settings={settings} set={set} />
            <div className="md:col-span-2">
              <Field label="URL botón" value={settings.home_welcome_cta_url} onChange={(v) => set("home_welcome_cta_url", v)} />
            </div>
          </div>
        </Section>

        <Section title="Bloque Manifesto">
          <div className="grid md:grid-cols-2 gap-4">
            <MultiLangField label="Kicker" prefix="home_manifesto_kicker" settings={settings} set={set} />
            <MultiLangField label="Frase destacada" prefix="home_manifesto_quote" settings={settings} set={set} />
            <div className="md:col-span-2">
              <MultiLangField label="Texto" prefix="home_manifesto_body" settings={settings} set={set} textarea />
            </div>
            <div className="md:col-span-2">
              <Field label="URL Imagen fondo (paisaje ancho)" value={settings.home_manifesto_bg_image ?? ""} onChange={(v) => set("home_manifesto_bg_image", v)} />
            </div>
          </div>
        </Section>

        <Section title="Estadísticas y Ventajas — titulares">
          <div className="grid md:grid-cols-2 gap-4">
            <MultiLangField label="Título estadísticas" prefix="home_stats_title" settings={settings} set={set} />
            <MultiLangField label="Kicker ventajas" prefix="home_benefits_kicker" settings={settings} set={set} />
            <MultiLangField label="Título ventajas" prefix="home_benefits_title" settings={settings} set={set} />
            <MultiLangField label="Texto CTA ventajas" prefix="home_benefits_cta_label" settings={settings} set={set} />
          </div>
        </Section>
      </div>
    </div>
  );
}
